
import { CreateElement, VNode } from "vue";
import { Component, Vue, Prop } from "vue-property-decorator";

import { Question, QuestionType, Answer, TPTPlaceResult } from "@/interfaces";

import NewSurveyRepeaterDialog from "@/components/NewSurveyRepeaterDialog.vue";
import SelectUniqueInsertDialog from "@/components/SelectUniqueInsertDialog.vue";
import { cloneDeep, uniqBy } from "lodash";
import DetailsDialog from "./DetailsDialog.vue";
import { QPopupProxy, QSelect } from "quasar";

@Component({
  components: {
    SurveyMap: () => import("@/components/SurveyMap.vue"),
  },
})
export default class SurveyFields extends Vue {
  @Prop({ type: Array, required: true })
  questions!: Question[];

  @Prop({ type: Array, required: true })
  allQuestions!: Question[];

  @Prop({ type: Array, default: () => [] })
  value!: Answer[];

  answers: Answer[] = [];

  filterArrays: any = [];
  newValueArrays: any = [];

  created() {
    this.answers = this.value;
  }

  render(h: CreateElement) {
    const elements: VNode[] = [];

    for (const question of this.questions) {
      const columnElements: VNode[] = [];

      const labelElements: VNode[] = [];

      const spanRegex = /<span>([^<]*?)<\/span>/g;
      const replaceRegex = /(<([^>]+)>|&[^;]+;)/gi;
      const matchTitle = question.title.match(spanRegex);
      let mappedTitle: any = [question.title];

      if (matchTitle && matchTitle.length > 0) {
        const index = question.title.indexOf(matchTitle[0]);
        const beforeText = question.title.substring(0, index);
        const afterText = question.title.substring(index + matchTitle[0].length);
        const anchorText = this.createAnchorText(matchTitle[0].replace(replaceRegex, ""), h);
        mappedTitle = [beforeText, anchorText, afterText];
      }

      const labelElement = h(
        "p",
        {
          class: "text-weight-bold text-dark-secondary noto-sans full-width",
        },
        [`${question.index}.${question.subIndex ? question.subIndex : ""} `, [...mappedTitle]],
      );

      labelElements.push(labelElement);

      if (question.subtitle) {
        const matchSubtitle = question.subtitle.match(spanRegex);
        let mappedSubtitle: any = [question.subtitle];

        if (matchSubtitle && matchSubtitle.length > 0) {
          const index = question.subtitle.indexOf(matchSubtitle[0]);
          const beforeText = question.subtitle.substring(0, index);
          const afterText = question.subtitle.substring(index + matchSubtitle[0].length);
          const anchorText = this.createAnchorText(matchSubtitle[0].replace(replaceRegex, ""), h);
          mappedSubtitle = [beforeText, anchorText, afterText];
        }

        const subtitleElement = h(
          "p",
          {
            class: "text-subtitle2 text-grey noto-sans full-width",
          },
          mappedSubtitle,
        );

        labelElements.push(subtitleElement);
      }

      const labelElementWrapper = h(
        "div",
        {
          class: `${
            question.type === QuestionType.CHECKBOX || question.type === QuestionType.CHECKBOX_WITH_MAP
              ? "col-12"
              : "col-4"
          }`,
        },
        labelElements,
      );

      columnElements.push(labelElementWrapper);

      let canShow = true;
      let node: VNode | null = null;
      let groupedNodes: VNode[] = [];

      if (question.conf && question.conf.show_on) {
        const showOn = question.conf.show_on;
        const bindKey = "id";
        const valueKey = "value";
        const modelIndex = this.answers.findIndex(answer => answer.id === showOn[bindKey]);

        if (modelIndex !== -1) {
          const answer = this.answers[modelIndex].answer;

          if (answer) {
            canShow = answer === showOn[valueKey];
          } else {
            canShow = false;
          }
        }
      }

      if (canShow) {
        switch (question.type) {
          case QuestionType.SELECT:
            node = this.createSelectInput(question, h);
            break;
          case QuestionType.MULTI_SELECT:
            node = this.createSelectInput(question, h, true);
            break;
          case QuestionType.CHECKBOX:
            node = this.createCheckboxInput(question, h);
            break;
          case QuestionType.CHECKBOX_WITH_MAP:
            node = this.createCheckboxMapInput(question, h);
            break;
          case QuestionType.TEXT:
            node = this.createTextInput(question, h);
            break;
          case QuestionType.TEXT_NUMBER:
            node = this.createTextNumberInput(question, h);
            break;
          case QuestionType.GROUP:
            groupedNodes = this.createGroupInput(question, h);
            break;
          case QuestionType.REPEATER:
            groupedNodes = this.createRepeaterInput(question, h);
            break;
          default:
            break;
        }

        if (groupedNodes.length > 0) {
          for (const groupedNode of groupedNodes) {
            columnElements.push(groupedNode);
          }
        }

        if (node) {
          columnElements.push(node);
        }

        if (node || groupedNodes.length > 0) {
          const column = h("div", { class: "row q-col-gutter-x-xl" }, columnElements);
          elements.push(column);
        }
      }
    }

    return h("div", elements);
  }

  createSelectInput(
    question: Question,
    h: CreateElement,
    multiple = false,
    classes = "column col-8 survey-field",
    parentGroupId = "",
  ): VNode | null {
    let modelIndex = -1;

    if (parentGroupId !== "") {
      modelIndex = this.answers.findIndex(answer => answer.id === parentGroupId);
    } else {
      modelIndex = this.answers.findIndex(answer => answer.id === question.id);
    }

    if (modelIndex === -1) {
      return null;
    }

    const emitProps = {
      "emit-value": true,
      "map-options": true,
    };

    let styleProps: any = {
      "use-chips": multiple,
      "use-input": true,
      "input-debounce": 0,
    };

    if (question.conf && question.conf.readonly) {
      styleProps = {
        ...styleProps,
        readonly: true,
      };
    }

    if (question.conf && question.conf.allow_insert) {
      styleProps = {
        ...styleProps,
        "new-value-mode": "add-unique",
      };
    }

    const rules = [];

    if (multiple && question.conf) {
      if (question.conf.min) {
        const min = question.conf.min;
        rules.push(
          (val: any) => (val !== null && val.length >= min) || `E' necessario selezionare almeno ${min} opzioni`,
        );
      }
      if (question.conf.max) {
        const max = question.conf.max;
        rules.push(
          (val: any) => (val !== null && val.length <= max) || `Non è possibile inserire più di ${max} opzioni`,
        );
      }
    }

    let options: any = question.options;

    if (Array.isArray(question.options)) {
      if (typeof question.options[0] === "object") {
        options = options.map((opt: any) => ({
          label: opt.name,
          value: opt.id,
        }));
      }
    }

    if (question.conf && question.conf.bind) {
      const bindIds = Array.isArray(question.conf.bind) ? question.conf.bind : [question.conf.bind];
      let modelsUnique: string[] = [];
      const bindModels = [...this.answers.filter(answer => bindIds.includes(answer.id) && answer.answer)].map(model => {
        const parent = this.allQuestions.find(question => question.id === model.id);

        if (parent) {
          if (
            parent.options &&
            parent.options.length > 0 &&
            Array.isArray(parent.options) &&
            typeof parent.options[0] === "object"
          ) {
            if (Array.isArray(model.answer)) {
              const options = model.answer.map((ans: any) => {
                if (Array.isArray(parent.options)) {
                  const findOption: any = parent.options.find((opt: any) => opt.id === ans);

                  if (findOption) {
                    const opt = {
                      label: findOption.name,
                      value: findOption.id,
                    };
                    return opt;
                  }
                }

                if (typeof ans === "string" && ans.startsWith("_")) {
                  return {
                    label: ans.split("_")[1],
                    value: ans,
                  };
                }

                return ans;
              });

              return options;
            } else {
              const options = [];
              const ans = model.answer;
              if (Array.isArray(parent.options)) {
                const findOption: any = parent.options.find((opt: any) => opt.id === ans);

                if (findOption) {
                  const opt = {
                    label: findOption.name,
                    value: findOption.id,
                  };
                  return opt;
                }
              }

              if (typeof ans === "string" && ans.startsWith("_")) {
                return {
                  label: ans.split("_")[1],
                  value: ans,
                };
              }

              options.push(ans);
            }
          } else if (parent.type === QuestionType.REPEATER) {
            if (question.conf && question.conf.bind_inner && parent.elements) {
              const bindInner = question.conf.bind_inner;
              const item = parent.elements.items.find(item => item.id === bindInner);
              let options = [];

              if (item) {
                if (item.options) {
                  let flatOptions: any[] = [];
                  if (Array.isArray(item.options) && item.options.length > 0 && typeof item.options[0] === "string") {
                    flatOptions = [...item.options];
                  } else {
                    if (Array.isArray(item.options)) {
                      item.options.forEach(opt => {
                        flatOptions = [...flatOptions, ...opt];
                      });
                    } else {
                      const itemOptions = item.options as { [key: string]: any };
                      for (const [key, value] of Object.entries(itemOptions)) {
                        if (itemOptions[key]) {
                          flatOptions = [...flatOptions, ...value];
                        }
                      }
                    }
                  }

                  const values = model.answer.map((ans: any) => ans[bindInner]);
                  options = flatOptions
                    .filter(flatOpt => {
                      if (typeof flatOpt === "string") {
                        return values.includes(flatOpt);
                      } else {
                        return values.includes(flatOpt.id);
                      }
                    })
                    .map((opt: any) => {
                      if (typeof opt === "string") {
                        return {
                          label: opt.startsWith("_") ? opt.split("_")[1] : opt,
                          value: opt,
                        };
                      } else {
                        return {
                          label: opt.name,
                          value: opt.id,
                        };
                      }
                    });
                  if (values.length > 0) {
                    values.forEach((value: any) => {
                      if (typeof value === "string" && value.startsWith("_")) {
                        options.push({
                          label: value.split("_")[1],
                          value,
                        });
                      }
                    });
                  }
                } else if (item.conf && item.conf.bind) {
                  const parentInnerBind = Array.isArray(item.conf.bind) ? item.conf.bind : [item.conf.bind];
                  const parentInner = this.allQuestions.find(question => parentInnerBind.includes(question.id));

                  if (parentInner) {
                    options = model.answer.map((ans: any) => {
                      if (Array.isArray(parentInner.options)) {
                        const findOption: any = parentInner.options.find((opt: any) => opt.id === ans[bindInner]);

                        if (findOption) {
                          const opt = {
                            label: findOption.name,
                            value: findOption.id,
                          };
                          return opt;
                        }
                      }

                      if (typeof ans === "string" && ans.startsWith("_")) {
                        return {
                          label: ans.split("_")[1],
                          value: ans,
                        };
                      }

                      return ans;
                    });
                  }
                }
              }

              return options;
            }
          }
        }
        return model.answer;
      });

      if (bindModels.length > 0) {
        bindModels.forEach((bind: any) => {
          if (Array.isArray(bind)) {
            modelsUnique = [...modelsUnique, ...bind];
          } else {
            modelsUnique = [...modelsUnique, bind];
          }
        });
        modelsUnique = [...new Set(modelsUnique)];

        if (question.options) {
          options = [];
          if (modelsUnique.length > 0 && typeof modelsUnique[0] === "object") {
            modelsUnique = uniqBy(modelsUnique, "value");
            modelsUnique.forEach((modelTemp: any) => {
              const model = modelTemp.value;
              const opts = question.options as { [key: string]: any };
              if (opts[model] && opts[model].length > 0) {
                if (typeof opts[model][0] === "object") {
                  options = [
                    ...options,
                    ...opts[model].map((opt: any) => ({
                      label: opt.name,
                      value: opt.id,
                    })),
                  ];

                  options = uniqBy(options, "value");
                } else {
                  options = [...options, ...opts[model]];
                }
              }
            });
          } else {
            modelsUnique.forEach((model: string) => {
              const opts = question.options as { [key: string]: any };
              if (opts[model] && opts[model].length > 0) {
                if (typeof opts[model][0] === "object") {
                  options = [
                    ...options,
                    ...opts[model].map((opt: any) => ({
                      label: opt.name,
                      value: opt.id,
                    })),
                  ];
                } else {
                  options = [...options, ...opts[model]];
                }
              }
            });
          }
        } else if (Array.isArray(modelsUnique) && modelsUnique.length > 0 && Array.isArray(modelsUnique[0])) {
          options = [];
          modelsUnique.forEach((model: any) => {
            if (Array.isArray(model) && model.length > 0 && typeof model[0] === "object") {
              options = [...options, ...uniqBy(model, "value")];
            } else {
              options = [...options, ...model];
            }
          });
        } else {
          options = [...modelsUnique];
        }
      } else {
        options = [];
      }
    }

    const id = question.id + parentGroupId;
    const findArr = this.filterArrays.find((filarr: any) => filarr.id === id);
    const findArrNewValue = this.newValueArrays.find((filarr: any) => filarr.id === id);
    const originalOptions = options;

    let newValueOptions = [];

    if (!findArrNewValue) {
      this.newValueArrays.push({
        id,
        newValues: [],
      });
    } else {
      newValueOptions = findArrNewValue.newValues;
    }

    if (!findArr) {
      if (options.length > 0) {
        this.filterArrays.push({
          id,
          filteredOptions: options,
        });
      }
    } else {
      options = findArr.filteredOptions;
    }

    const selectSlotChilds = [];

    if (question.conf && question.conf.allow_insert) {
      const allowInsertBtn = h("q-btn", {
        class: "allow-insert-slot__btn",
        props: {
          round: true,
          dense: true,
          icon: "add",
          color: "secondary",
          size: "sm",
        },
      });

      const allowInsertDiv = h(
        "div",
        {
          class: "relative-position allow-insert-slot text-weight-medium",
          slot: "after-options",
          on: {
            click: () => {
              this.$q
                .dialog({
                  component: SelectUniqueInsertDialog,
                })
                .onOk((newAllowInsertValue: string) => {
                  let finalValue = newAllowInsertValue;

                  if (
                    (originalOptions.length > 0 && typeof originalOptions[0] === "object") ||
                    (question.conf && question.conf.insert_type === "object")
                  ) {
                    const findArrNewValueIndex = this.newValueArrays.findIndex((filarr: any) => filarr.id === id);
                    if (findArrNewValueIndex !== -1) {
                      const find = this.newValueArrays[findArrNewValueIndex].newValues.find(
                        (newValue: any) => newValue.value === `_${newAllowInsertValue}`,
                      );

                      if (!find) {
                        this.$set(this.newValueArrays, findArrNewValueIndex, {
                          id: this.newValueArrays[findArrNewValueIndex].id,
                          newValues: [
                            ...this.newValueArrays[findArrNewValueIndex].newValues,
                            {
                              label: newAllowInsertValue,
                              value: `_${newAllowInsertValue}`,
                            },
                          ],
                        });
                      }

                      finalValue = `_${newAllowInsertValue}`;
                    }
                  } else {
                    finalValue = newAllowInsertValue;
                  }

                  if (this.$refs[question.id]) {
                    const questionSelect: any = this.$refs[question.id];
                    questionSelect.add(finalValue, true);
                    questionSelect.options.push(finalValue);
                    questionSelect.refresh();
                  }
                });
            },
          },
          directives: [
            {
              name: "ripple",
            },
          ],
        },
        [allowInsertBtn, "Inserisci nuovo"],
      );

      const allowInsertDivNoResults = h(
        "div",
        {
          class: "relative-position allow-insert-slot text-weight-medium",
          slot: "no-option",
          on: {
            click: () => {
              this.$q
                .dialog({
                  component: SelectUniqueInsertDialog,
                })
                .onOk((newAllowInsertValue: string) => {
                  let finalValue = newAllowInsertValue;

                  if (
                    (originalOptions.length > 0 && typeof originalOptions[0] === "object") ||
                    (question.conf && question.conf.insert_type === "object")
                  ) {
                    const findArrNewValueIndex = this.newValueArrays.findIndex((filarr: any) => filarr.id === id);
                    if (findArrNewValueIndex !== -1) {
                      const find = this.newValueArrays[findArrNewValueIndex].newValues.find(
                        (newValue: any) => newValue.value === `_${newAllowInsertValue}`,
                      );

                      if (!find) {
                        this.$set(this.newValueArrays, findArrNewValueIndex, {
                          id: this.newValueArrays[findArrNewValueIndex].id,
                          newValues: [
                            ...this.newValueArrays[findArrNewValueIndex].newValues,
                            {
                              label: newAllowInsertValue,
                              value: `_${newAllowInsertValue}`,
                            },
                          ],
                        });
                      }

                      finalValue = `_${newAllowInsertValue}`;
                    }
                  } else {
                    finalValue = newAllowInsertValue;
                  }

                  if (this.$refs[question.id]) {
                    const questionSelect: any = this.$refs[question.id];
                    questionSelect.add(finalValue, true);
                    questionSelect.options.push(finalValue);
                    questionSelect.refresh();
                  }
                });
            },
          },
          directives: [
            {
              name: "ripple",
            },
          ],
        },
        [allowInsertBtn, "Inserisci nuovo"],
      );

      selectSlotChilds.push(allowInsertDiv);
      selectSlotChilds.push(allowInsertDivNoResults);
    }

    const allowInsertEvent = {
      "new-value": (val: string, done: (val: any, type: string) => void) => {
        if (question.conf && question.conf.allow_insert) {
          if (
            (originalOptions.length > 0 && typeof originalOptions[0] === "object") ||
            (question.conf && question.conf.insert_type === "object")
          ) {
            const findArrNewValueIndex = this.newValueArrays.findIndex((filarr: any) => filarr.id === id);
            if (findArrNewValueIndex !== -1) {
              const find = this.newValueArrays[findArrNewValueIndex].newValues.find(
                (newValue: any) => newValue.value === `_${val}`,
              );

              if (!find) {
                this.$set(this.newValueArrays, findArrNewValueIndex, {
                  id: this.newValueArrays[findArrNewValueIndex].id,
                  newValues: [
                    ...this.newValueArrays[findArrNewValueIndex].newValues,
                    {
                      label: val,
                      value: `_${val}`,
                    },
                  ],
                });
              }

              done(`_${val}`, "add-unique");
            }
          } else {
            done(val, "add-unique");
          }
        }
      },
    };

    return h(
      "q-select",
      {
        ref: question.id,
        class: classes,
        props: {
          // eslint-disable-next-line prettier/prettier
          "value": parentGroupId === ""
            ? this.answers[modelIndex].answer : this.answers[modelIndex].answer[question.id],
          // eslint-disable-next-line prettier/prettier
          "options": [...options, ...newValueOptions],
          // eslint-disable-next-line prettier/prettier
          "disable": originalOptions.length === 0 && (question.conf && !question.conf.allow_insert),
          // eslint-disable-next-line prettier/prettier
          "hint": question.conf && question.conf.placeholder ? question.conf.placeholder : "",
          multiple,
          rules,
          // eslint-disable-next-line prettier/prettier
          "behavior": question.conf && question.conf.details ? "dialog" : "menu",
          "popup-content-class": question.conf && question.conf.details ? "custom-dialog-scroll" : "",
          ...styleProps,
          ...emitProps,
          ...this.selectOptions,
        },
        on: {
          ...allowInsertEvent,
          remove: (details: any) => {
            const findArrNewValueIndex = this.newValueArrays.findIndex((filarr: any) => filarr.id === id);
            if (findArrNewValueIndex !== -1) {
              const findArrNewValue = Object.assign({}, this.newValueArrays[findArrNewValueIndex]);

              const newValues = findArrNewValue.newValues.filter((val: any) => val.value !== details.value);
              this.$set(this.newValueArrays, findArrNewValueIndex, {
                id: findArrNewValue.id,
                newValues,
              });
            }
          },
          filter: (value: string, update: (fun: () => void) => void) => {
            update(() => {
              const id = question.id + parentGroupId;
              const findArrIndex = this.filterArrays.findIndex((filarr: any) => filarr.id === id);

              if (findArrIndex !== -1) {
                let isObject = false;
                const tempOptions: any = question.options;
                if (tempOptions && tempOptions.length > 0) {
                  if (typeof tempOptions[0] === "object") {
                    isObject = true;
                    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
                    // @ts-ignore
                    options = tempOptions.map((opt: any) => ({
                      label: opt.name,
                      value: opt.id,
                    }));
                  } else {
                    options = tempOptions;
                  }
                }

                const findArr = this.filterArrays[findArrIndex];
                let newOptions = [...options];
                if (value !== "") {
                  if (isObject) {
                    newOptions = newOptions.filter((opt: any) => opt.label.toLowerCase().includes(value.toLowerCase()));
                  } else {
                    newOptions = newOptions.filter((opt: any) => {
                      if (typeof opt === "object") {
                        return opt.label.toLowerCase().includes(value.toLowerCase());
                      }

                      return opt.toLowerCase().includes(value.toLowerCase());
                    });
                  }
                }

                this.$set(findArr, "filteredOptions", newOptions);
                this.$set(this.filterArrays, findArrIndex, findArr);
              }
            });
          },
          input: (value: string) => {
            const answers = [...this.answers];

            if (parentGroupId === "") {
              this.$set(answers, modelIndex, { ...answers[modelIndex], answer: value });
            } else {
              const answer: { [Key: string]: any } = { ...answers[modelIndex].answer };
              this.$set(answer, question.id, value);
              this.$set(answers, modelIndex, { ...answers[modelIndex], answer });
            }

            const bindedFields = this.allQuestions.filter(
              qs => qs.conf && qs.conf.bind && qs.conf.bind.includes(question.id),
            );

            if (bindedFields.length > 0) {
              bindedFields.forEach(bindedField => {
                const bindedAnswerIndex = answers.findIndex(answer => answer.id === bindedField.id);

                if (bindedAnswerIndex !== -1) {
                  let condition =
                    answers[bindedAnswerIndex].answer !== null ||
                    (Array.isArray(answers[bindedAnswerIndex].answer) && answers[bindedAnswerIndex].answer.length > 0);
                  if (answers[bindedAnswerIndex].answer !== null && !Array.isArray(answers[bindedAnswerIndex].answer)) {
                    condition = condition || answers[bindedAnswerIndex].answer.value !== value;
                  }

                  if (condition) {
                    this.$set(answers, bindedAnswerIndex, {
                      id: answers[bindedAnswerIndex].id,
                      answer: null,
                    });
                  }

                  this.filterArrays = this.filterArrays.filter(
                    (filarr: any) => filarr.id !== answers[bindedAnswerIndex].id,
                  );
                }
              });
            }

            this.answers = [...answers];
            this.$emit("input", this.answers);
          },
        },
        scopedSlots:
          question.conf && question.conf.details
            ? {
                option: slotProps => {
                  const itemChilds: VNode[] = [];
                  const optLabel = h("q-item-label", slotProps.opt.label);
                  const optLabelSection = h("q-item-section", [optLabel]);
                  itemChilds.push(optLabelSection);

                  const optDetails = h("q-btn", {
                    class: "table-button",
                    props: {
                      // eslint-disable-next-line prettier/prettier
                      "label": "Visualizza dettagli",
                      // eslint-disable-next-line prettier/prettier
                      "dense": true,
                      // eslint-disable-next-line prettier/prettier
                      "flat": true,
                      // eslint-disable-next-line prettier/prettier
                      "padding": "0px",
                      // eslint-disable-next-line prettier/prettier
                      "text-color": "secondary",
                    },
                    on: {
                      click: (event: MouseEvent) => {
                        event.preventDefault();
                        event.stopPropagation();
                        this.$q
                          .dialog({
                            component: DetailsDialog,
                            parent: this,
                            title: question.conf?.details.title,
                            id: slotProps.opt.value,
                          })
                          .onOk(() => {
                            const value = slotProps.opt.value;
                            const answers = [...this.answers];

                            if (parentGroupId === "") {
                              this.$set(answers, modelIndex, { ...answers[modelIndex], answer: value });
                            } else {
                              const answer: { [Key: string]: any } = { ...answers[modelIndex].answer };
                              this.$set(answer, question.id, value);
                              this.$set(answers, modelIndex, { ...answers[modelIndex], answer });
                            }

                            const bindedFields = this.allQuestions.filter(
                              qs => qs.conf && qs.conf.bind && qs.conf.bind.includes(question.id),
                            );

                            if (bindedFields.length > 0) {
                              bindedFields.forEach(bindedField => {
                                const bindedAnswerIndex = answers.findIndex(answer => answer.id === bindedField.id);

                                if (bindedAnswerIndex !== -1) {
                                  let condition =
                                    answers[bindedAnswerIndex].answer !== null ||
                                    (Array.isArray(answers[bindedAnswerIndex].answer) &&
                                      answers[bindedAnswerIndex].answer.length > 0);
                                  if (
                                    answers[bindedAnswerIndex].answer !== null &&
                                    !Array.isArray(answers[bindedAnswerIndex].answer)
                                  ) {
                                    condition = condition || answers[bindedAnswerIndex].answer.value !== value;
                                  }

                                  if (condition) {
                                    this.$set(answers, bindedAnswerIndex, {
                                      id: answers[bindedAnswerIndex].id,
                                      answer: null,
                                    });
                                  }

                                  this.filterArrays = this.filterArrays.filter(
                                    (filarr: any) => filarr.id !== answers[bindedAnswerIndex].id,
                                  );
                                }
                              });
                            }

                            this.answers = [...answers];
                            this.$emit("input", this.answers);

                            const selectRef = this.$refs[question.id] as QSelect;

                            if (selectRef) {
                              selectRef.hidePopup();
                            }
                          });
                      },
                    },
                  });
                  const optDetailsSection = h("q-item-section", { props: { side: true } }, [optDetails]);

                  itemChilds.push(optDetailsSection);
                  return h("q-item", { on: slotProps.itemEvents, props: slotProps.itemProps }, [...itemChilds]);
                },
              }
            : undefined,
      },
      selectSlotChilds,
    );
  }

  createCheckboxInput(
    question: Question,
    h: CreateElement,
    classes = "col-8 custom-options-group",
    parentGroupId = "",
  ): VNode | null {
    let modelIndex = -1;

    if (parentGroupId !== "") {
      modelIndex = this.answers.findIndex(answer => answer.id === parentGroupId);
    } else {
      modelIndex = this.answers.findIndex(answer => answer.id === question.id);
    }

    if (modelIndex === -1) {
      return null;
    }

    let styleProps = {};

    if (question.conf && question.conf.readonly) {
      styleProps = {
        ...styleProps,
        disable: true,
      };
    }

    let options: any = cloneDeep(question.options);
    const answers =
      parentGroupId === "" ? this.answers[modelIndex].answer : this.answers[modelIndex].answer[question.id];

    if (Array.isArray(question.options)) {
      if (question.options.length > 0) {
        if (typeof question.options[0] === "string") {
          options = (question.options as string[]).map(option => {
            let disable = false;

            if (question.conf && question.conf.max !== undefined) {
              disable =
                answers.find((ans: string) => ans === option) === undefined && answers.length === question.conf.max;
            }

            return {
              label: option,
              value: option,
              disable,
            };
          });
        } else {
          const questionOptions: any = cloneDeep(question.options);
          options = [];

          for (const option of questionOptions) {
            const opt = option as any;

            let disable = false;

            if (question.conf && question.conf.max !== undefined) {
              disable =
                answers.find((ans: string) => ans === opt.value) === undefined && answers.length === question.conf.max;
            }

            const labelToShow = opt.name;
            options.push({
              label: labelToShow,
              value: opt.id,
              disable,
            });
          }
        }
      }
    }

    if (question.conf && question.conf.bind && question.conf.bind.length > 0) {
      const bindIds = question.conf.bind;
      let modelsUnique: string[] = [];
      const bindModels = [...this.answers.filter(answer => bindIds.includes(answer.id) && answer.answer)].map(
        model => model.answer,
      );

      if (bindModels.length > 0) {
        bindModels.forEach((bind: string) => {
          modelsUnique = [...modelsUnique, bind];
        });
        modelsUnique = [...new Set(modelsUnique)];

        if (question.options) {
          options = [];
          modelsUnique.forEach((model: string) => {
            const opts = question.options as { [key: string]: any };
            if (opts[model] && opts[model].length > 0) {
              if (typeof opts[model][0] === "object") {
                options = [
                  ...options,
                  ...opts[model].map((opt: any) => ({
                    label: opt.name,
                    value: opt.id,
                  })),
                ];
              } else {
                options = [
                  ...options,
                  ...opts[model].map((opt: any) => ({
                    label: opt,
                    value: opt,
                  })),
                ];
              }
            }
          });
        } else {
          options = [...modelsUnique];
        }
      } else {
        options = [];
      }
    }

    const groupValue =
      parentGroupId === "" ? this.answers[modelIndex].answer : this.answers[modelIndex].answer[question.id];

    const optionGroup = h("q-option-group", {
      class: classes,
      props: {
        value: groupValue ? groupValue : [],
        options,
        ...styleProps,
        ...this.checkboxOptions,
      },
      on: {
        input: (value: any) => {
          const answers = [...this.answers];
          if (parentGroupId === "") {
            this.$set(answers, modelIndex, { ...answers[modelIndex], answer: value });
          } else {
            this.$set(answers[modelIndex].answer, question.id, value);
          }
          this.answers = [...answers];
          this.$emit("input", this.answers);
        },
      },
    });

    const value = parentGroupId === "" ? this.answers[modelIndex].answer : this.answers[modelIndex].answer[question.id];
    const fields = [optionGroup];

    if (question.conf && question.conf.selectall) {
      let modelValue: boolean | null = false;

      if (this.answers[modelIndex].answer) {
        if (this.answers[modelIndex].answer.length > 0) {
          modelValue = null;
        }

        if (this.answers[modelIndex].answer.length === options.length) {
          modelValue = true;
        }
      }

      const optionGroupAll = h("q-checkbox", {
        class: classes,
        props: {
          value: modelValue,
          label: "Seleziona Tutti",
          options,
          ...styleProps,
          ...this.checkboxOptions,
        },
        on: {
          input: (value: boolean) => {
            const answers = [...this.answers];

            const answer = value ? options.map((el: any) => el.value) : [];

            if (parentGroupId === "") {
              this.$set(answers, modelIndex, { ...answers[modelIndex], answer });
            } else {
              this.$set(answers[modelIndex].answer, question.id, answer);
            }
            this.answers = [...answers];
            this.$emit("input", this.answers);
          },
        },
      });

      fields.unshift(optionGroupAll);
    }

    let message = "";
    if (question.conf && question.conf.min && value.length < question.conf.min && value.length > 0) {
      message = `E' necessario selezionare almeno ${question.conf.min} opzioni`;
    }

    if (message !== "") {
      const errorMessage = h("div", message);
      const errorMessageCol = h("div", { class: "q-field__messages col" }, [errorMessage]);
      const errorMessageWrapper = h("div", { class: "q-field__bottom row items-start survey-field text-negative" }, [
        errorMessageCol,
      ]);

      fields.push(errorMessageWrapper);
    }
    return h("div", { class: "full-width survey-field" }, fields);
  }

  createCheckboxMapInput(question: Question, h: CreateElement, classes = "", parentGroupId = ""): VNode | null {
    let modelIndex = -1;

    if (parentGroupId !== "") {
      modelIndex = this.answers.findIndex(answer => answer.id === parentGroupId);
    } else {
      modelIndex = this.answers.findIndex(answer => answer.id === question.id);
    }

    if (modelIndex === -1) {
      return null;
    }

    if (Array.isArray(question.options)) {
      const optionGroup = h("SurveyMap", {
        class: classes,
        props: {
          value: parentGroupId === "" ? this.answers[modelIndex].answer : this.answers[modelIndex].answer[question.id],
          places: question.options,
          label: question.button_label,
          question,
          ...this.checkboxOptions,
        },
        on: {
          input: (values: TPTPlaceResult[]) => {
            const answers = [...this.answers];
            if (parentGroupId === "") {
              this.$set(answers, modelIndex, { ...answers[modelIndex], answer: values });
            } else {
              this.$set(answers[modelIndex].answer, question.id, values);
            }
            this.answers = [...answers];
            this.$emit("input", this.answers);
          },
        },
      });

      const value =
        parentGroupId === "" ? this.answers[modelIndex].answer : this.answers[modelIndex].answer[question.id];
      const fields = [optionGroup];

      let message = "";
      if (question.conf && question.conf.min && value.length < question.conf.min && value.length > 0) {
        message = `E' necessario selezionare almeno ${question.conf.min} opzioni`;
      }

      if (message !== "") {
        const errorMessage = h("div", message);
        const errorMessageCol = h("div", { class: "q-field__messages col" }, [errorMessage]);
        const errorMessageWrapper = h("div", { class: "q-field__bottom row items-start survey-field text-negative" }, [
          errorMessageCol,
        ]);

        fields.push(errorMessageWrapper);
      }
      return h("div", { class: "full-width survey-field" }, fields);
    }

    return null;
  }

  createTextInput(
    question: Question,
    h: CreateElement,
    classes = "column col-8 survey-field",
    parentGroupId = "",
  ): VNode | null {
    let modelIndex = -1;

    if (parentGroupId !== "") {
      modelIndex = this.answers.findIndex(answer => answer.id === parentGroupId);
    } else {
      modelIndex = this.answers.findIndex(answer => answer.id === question.id);
    }

    if (modelIndex === -1) {
      return null;
    }

    let styleProps = {};

    if (question.conf && question.conf.readonly) {
      styleProps = {
        ...styleProps,
        disable: true,
      };
    }

    const rules = [];

    if (question.conf) {
      if (question.conf.min) {
        const min = question.conf.min;
        rules.push(
          (val: any) => (val !== null && val.length >= min) || `E' necessario inserire almeno ${min} caratteri`,
        );
      }
      if (question.conf.max) {
        const max = question.conf.max;
        rules.push(
          (val: any) => (val !== null && val.length <= max) || `Non è possibile inserire più di ${max} caratteri`,
        );
      }
    }

    const template: VNode[] = [];
    const dateRef = question.id + "-qDateProxy";

    if (question.conf && question.conf.type && question.conf.type === "date") {
      const inputDate = h("q-date", {
        ref: "q-date-picker",
        props: {
          value: parentGroupId === "" ? this.answers[modelIndex].answer : this.answers[modelIndex].answer[question.id],
          color: "secondary",
          minimal: true,
        },
        on: {
          input: (value: string) => {
            const answers = [...this.answers];

            if (parentGroupId === "") {
              this.$set(answers, modelIndex, { ...answers[modelIndex], answer: value });
            } else {
              const answer: { [Key: string]: any } = { ...answers[modelIndex].answer };
              this.$set(answer, question.id, value);
              this.$set(answers, modelIndex, { ...answers[modelIndex], answer });
            }

            this.answers = [...answers];
            this.$emit("input", this.answers);
          },
        },
      });

      const popup = h(
        "q-popup-proxy",
        {
          ref: dateRef,
          props: {
            "transition-show": "scale",
            "transition-hide": "scale",
          },
        },
        [inputDate],
      );

      const icon = h(
        "q-icon",
        {
          class: "cursor-pointer survey-data-icon",
          props: {
            name: "event",
            size: "md",
            color: "secondary",
          },
        },
        [popup],
      );

      template.push(icon);
      rules.push("date");
    }

    return h(
      "q-input",
      {
        class: classes,
        props: {
          value: parentGroupId === "" ? this.answers[modelIndex].answer : this.answers[modelIndex].answer[question.id],
          hint: question.conf && question.conf.placeholder ? question.conf.placeholder : "",
          type: question.conf && question.conf.type && question.conf.type !== "date" ? question.conf.type : "text",
          pattern: question.conf && question.conf.pattern ? question.conf.pattern : "",
          maxlength: question.conf ? question.conf.max : null,
          minlength: question.conf ? question.conf.min : null,
          mask: question.conf && question.conf.type && question.conf.type === "date" ? "date" : undefined,
          rules,
          ...this.inputOptions,
          ...styleProps,
        },
        on: {
          input: (value: string) => {
            const answers = [...this.answers];

            if (parentGroupId === "") {
              this.$set(answers, modelIndex, { ...answers[modelIndex], answer: value });
            } else {
              const answer: { [Key: string]: any } = { ...answers[modelIndex].answer };
              this.$set(answer, question.id, value);
              this.$set(answers, modelIndex, { ...answers[modelIndex], answer });
            }

            this.answers = [...answers];
            this.$emit("input", this.answers);
          },
          focus: () => {
            if (question.conf && question.conf.type && question.conf.type === "date") {
              const popupRef = this.$refs[dateRef] as QPopupProxy;

              if (popupRef) {
                popupRef.show();
              }
            }
          },
        },
      },
      [...template],
    );
  }

  createTextNumberInput(
    question: Question,
    h: CreateElement,
    classes = "column col-8 survey-field",
    parentGroupId = "",
  ): VNode | null {
    let modelIndex = -1;

    if (parentGroupId !== "") {
      modelIndex = this.answers.findIndex(answer => answer.id === parentGroupId);
    } else {
      modelIndex = this.answers.findIndex(answer => answer.id === question.id);
    }

    if (modelIndex === -1) {
      return null;
    }

    let styleProps = {};

    if (question.conf && question.conf.readonly) {
      styleProps = {
        ...styleProps,
        disable: true,
      };
    }

    const isPercentage = question.conf && question.conf.type === "percentage";

    return h("q-input", {
      class: classes,
      props: {
        value: parentGroupId === "" ? this.answers[modelIndex].answer : this.answers[modelIndex].answer[question.id],
        type: "number",
        hint: question.conf && question.conf.placeholder ? question.conf.placeholder : "",
        pattern: question.conf && question.conf.pattern ? question.conf.pattern : "",
        max: question.conf ? question.conf.max : null,
        min: question.conf ? question.conf.min : null,
        suffix: isPercentage ? "%" : "",
        rules: isPercentage
          ? [
              (val: string) => {
                const value = parseInt(val);
                return value >= 0 || "Inserire un valore maggiore di zero";
              },
              (val: string) => {
                const value = parseInt(val);
                return value <= 100 || "Inserire un valore minore di cento";
              },
            ]
          : [],
        ...this.inputOptions,
        ...styleProps,
      },
      on: {
        input: (value: string) => {
          const intValue = parseInt(value);
          const answers = [...this.answers];

          if (parentGroupId === "") {
            this.$set(answers, modelIndex, { ...answers[modelIndex], answer: intValue });
          } else {
            const answer: { [Key: string]: any } = { ...answers[modelIndex].answer };
            this.$set(answer, question.id, intValue);
            this.$set(answers, modelIndex, { ...answers[modelIndex], answer });
          }

          this.answers = [...answers];
          this.$emit("input", this.answers);
        },
      },
    });
  }

  createGroupInput(question: Question, h: CreateElement): VNode[] {
    const modelIndex = this.answers.findIndex(answer => answer.id === question.id);

    if (modelIndex === -1) {
      return [];
    }

    const nodes: VNode[] = [];
    const group: VNode[] = [];

    if (question.items && question.items.length > 0) {
      question.items.forEach(item => {
        let canShow = true;

        if (item.conf && item.conf.show_on) {
          const showOn = item.conf.show_on;
          const bindKey = "id";
          const valueKey = "value";
          const modelIndex = this.answers.findIndex(answer => answer.id === question.id);

          if (modelIndex !== -1) {
            const answer = this.answers[modelIndex].answer;

            if (answer) {
              if (showOn[bindKey] in answer) {
                const key = showOn[bindKey];
                canShow = answer[key] === showOn[valueKey];
              } else {
                canShow = false;
              }
            } else {
              canShow = false;
            }
          }
        }

        const classes = "survey-field-group full-width";
        let node = null;

        if (canShow) {
          switch (item.type) {
            case QuestionType.SELECT:
              node = this.createSelectInput(item, h, false, classes, question.id);
              break;
            case QuestionType.MULTI_SELECT:
              node = this.createSelectInput(item, h, true, classes, question.id);
              break;
            case QuestionType.CHECKBOX:
              node = this.createCheckboxInput(item, h, classes + " custom-options-group-nested", question.id);
              break;
            case QuestionType.TEXT:
              node = this.createTextInput(item, h, classes, question.id);
              break;
            case QuestionType.TEXT_NUMBER:
              node = this.createTextNumberInput(item, h, classes, question.id);
              break;
            default:
              break;
          }
        }

        if (node !== null) {
          const elements = [node];
          const label =
            item.title && item.title !== ""
              ? h("p", { class: "text-subtitle2 text-secondary text-weight-bold" }, item.title)
              : null;
          if (label) {
            elements.unshift(label);
          }
          const column = h("div", { class: "full-width" }, elements);
          group.push(column);
        }
      });
    }

    nodes.push(h("div", { class: "column col-8" }, group));

    return nodes;
  }

  createRepeaterInput(question: Question, h: CreateElement) {
    const modelIndex = this.answers.findIndex(answer => answer.id === question.id);

    if (modelIndex === -1) {
      return [];
    }

    const columns: VNode[] = [];

    const addBtn = h("q-btn", {
      class: "survey-field survey-button",
      props: { label: "Aggiungi", color: "secondary", rounded: true },
      on: {
        click: () => {
          if (question.elements) {
            this.$q
              .dialog({
                component: NewSurveyRepeaterDialog,
                parent: this,
                title: question.elements.title,
                fields: question.elements.items,
                questions: this.questions,
                repeaterQuestion: question,
              })
              .onOk((answer: { [Key: string]: any }) => {
                const answers = cloneDeep(this.answers);
                const ans = cloneDeep(this.answers[modelIndex]);
                this.$set(ans, "answer", [...ans.answer, answer]);
                this.$set(answers, modelIndex, ans);
                const bindedFields = this.allQuestions.filter(
                  qs => qs.conf && qs.conf.bind && qs.conf.bind.includes(question.id),
                );

                if (bindedFields.length > 0) {
                  bindedFields.forEach(bindedField => {
                    const bindedAnswerIndex = answers.findIndex(answer => answer.id === bindedField.id);

                    if (bindedAnswerIndex !== -1) {
                      this.$set(answers, bindedAnswerIndex, {
                        id: answers[bindedAnswerIndex].id,
                        answer: null,
                      });

                      this.filterArrays = this.filterArrays.filter(
                        (filarr: any) => filarr.id !== answers[bindedAnswerIndex].id,
                      );
                    }
                  });
                }
                this.answers = [...answers];
                this.$emit("input", this.answers);
              });
          }
        },
      },
    });

    columns.push(h("div", { class: "column col-8" }, [addBtn]));

    const label = h(
      "p",
      {
        class: "col-12 text-secondary text-subtitle2 noto-sans text-weight-bold",
      },
      `${this.answers[modelIndex].answer.length} elementi inseriti`,
    );

    columns.push(label);

    if (this.answers[modelIndex].answer.length > 0) {
      const tableRows: VNode[] = [];

      this.answers[modelIndex].answer.forEach((el: any, index: number) => {
        const childs: VNode[] = [];

        for (const objectKey in el) {
          // eslint-disable-next-line no-prototype-builtins
          if (el.hasOwnProperty(objectKey) && question.elements) {
            let text = el[objectKey];
            const item = question.elements.items.find(it => it.id === objectKey);

            if (item && item.conf && item.conf.bind) {
              const parentQuestionBind = Array.isArray(item.conf.bind) ? item.conf.bind : [item.conf.bind];
              const parentQuestion = this.questions.find(it => parentQuestionBind.includes(it.id));

              if (parentQuestion && parentQuestion.options && Array.isArray(parentQuestion.options)) {
                if (Array.isArray(el[objectKey])) {
                  const objects = el[objectKey];
                  const objectsStrings: string[] = [];

                  objects.forEach((object: any) => {
                    if (parentQuestion.options && Array.isArray(parentQuestion.options)) {
                      const findOpt = parentQuestion.options.find((opt: any) => opt.id === object) as any;

                      if (findOpt) {
                        objectsStrings.push(findOpt.name);
                      } else {
                        const textString = text as string;
                        if (textString.startsWith("_")) {
                          objectsStrings.push(textString.split("_")[1]);
                        }
                      }
                    }
                  });

                  text = objectsStrings.join(", ");
                } else {
                  const findOpt = parentQuestion.options.find((opt: any) => opt.id === text) as any;

                  if (findOpt) {
                    text = findOpt.name;
                  } else {
                    const textString = text as string;
                    if (textString.startsWith("_")) {
                      text = textString.split("_")[1];
                    }
                  }
                }
              } else if (item.options) {
                let flatOptions: any[] = [];
                if (Array.isArray(item.options) && item.options.length > 0 && typeof item.options[0] === "string") {
                  flatOptions = [...item.options];
                } else {
                  if (Array.isArray(item.options)) {
                    item.options.forEach(opt => {
                      flatOptions = [...flatOptions, ...opt];
                    });
                  } else {
                    const itemOptions = item.options as { [key: string]: any };
                    for (const [key, value] of Object.entries(itemOptions)) {
                      if (itemOptions[key]) {
                        flatOptions = [...flatOptions, ...value];
                      }
                    }
                  }
                }

                if (flatOptions.length > 0) {
                  if (Array.isArray(el[objectKey])) {
                    const objects = el[objectKey];
                    const objectsStrings: string[] = [];

                    objects.forEach((object: any) => {
                      if (flatOptions && Array.isArray(flatOptions)) {
                        const findOpt = flatOptions.find((opt: any) => opt.id === object) as any;

                        if (findOpt) {
                          objectsStrings.push(findOpt.name);
                        } else {
                          const textString = text as string;
                          if (textString.startsWith("_")) {
                            objectsStrings.push(textString.split("_")[1]);
                          }
                        }
                      }
                    });

                    text = objectsStrings.join(", ");
                  } else {
                    const findOpt = flatOptions.find((opt: any) => opt.id === text) as any;

                    if (findOpt) {
                      text = findOpt.name;
                    } else {
                      const textString = text as string;
                      if (textString.startsWith("_")) {
                        text = textString.split("_")[1];
                      }
                    }
                  }
                } else {
                  const textString = text as string;
                  if (textString.startsWith("_")) {
                    text = textString.split("_")[1];
                  }
                }
              }
            } else if (
              item &&
              item.options &&
              Array.isArray(item.options) &&
              item.options.length > 0 &&
              typeof item.options[0] === "object"
            ) {
              if (Array.isArray(el[objectKey])) {
                const objects = el[objectKey];
                const objectsStrings: string[] = [];

                objects.forEach((object: any) => {
                  if (item.options && Array.isArray(item.options)) {
                    const findOpt = item.options.find((opt: any) => opt.id === object) as any;

                    if (findOpt) {
                      objectsStrings.push(findOpt.name);
                    } else {
                      const textString = text as string;
                      if (textString.startsWith("_")) {
                        objectsStrings.push(textString.split("_")[1]);
                      }
                    }
                  }
                });

                text = objectsStrings.join(", ");
              } else {
                const findOpt = item.options.find((opt: any) => opt.id === text) as any;

                if (findOpt) {
                  text = findOpt.name;
                } else {
                  const textString = text as string;
                  if (textString.startsWith("_")) {
                    text = textString.split("_")[1];
                  }
                }
              }
            } else if (Array.isArray(el[objectKey])) {
              text = el[objectKey].join(", ");
            } else if (item && item.conf && item.conf.type === "percentage") {
              text = `${text}%`;
            }

            childs.push(h("p", { class: "col text-grey text-subtitle2 table-text" }, text));
          }
        }

        const deleteBtn = h("q-btn", {
          class: "text-grey table-button",
          props: { label: "Rimuovi", dense: true, flat: true, ripple: false },
          on: {
            click: () => {
              const answers = cloneDeep(this.answers);
              const ans = Object.assign({}, this.answers[modelIndex]);
              const answer = cloneDeep(ans.answer);
              answer.splice(index, 1);
              this.$set(ans, "answer", answer);
              this.$set(answers, modelIndex, ans);
              const bindedFields = this.allQuestions.filter(
                qs => qs.conf && qs.conf.bind && qs.conf.bind.includes(question.id),
              );

              if (bindedFields.length > 0) {
                bindedFields.forEach(bindedField => {
                  const bindedAnswerIndex = answers.findIndex(answer => answer.id === bindedField.id);

                  if (bindedAnswerIndex !== -1) {
                    this.$set(answers, bindedAnswerIndex, {
                      id: answers[bindedAnswerIndex].id,
                      answer: null,
                    });

                    this.filterArrays = this.filterArrays.filter(
                      (filarr: any) => filarr.id !== answers[bindedAnswerIndex].id,
                    );
                  }
                });
              }
              this.answers = [...answers];
              this.$emit("input", this.answers);
            },
          },
        });

        childs.push(deleteBtn);

        tableRows.push(h("div", { class: "row g-gutter-x-md table-row" }, childs));
      });

      const table = h("div", { class: "col-12" }, tableRows);
      columns.push(table);
    }

    return columns;
  }

  createAnchorText(text: string, h: CreateElement): VNode {
    return h(
      "span",
      {
        class: "text-decoration-underline cursor-pointer",
        on: {
          click: () => {
            this.$emit("term:click", text);
          },
        },
      },
      text,
    );
  }

  get checkboxOptions() {
    return {
      type: "checkbox",
      color: "secondary",
      required: true,
    };
  }

  get selectOptions() {
    return {
      outlined: true,
      dense: true,
      color: "secondary",
      required: true,
    };
  }

  get inputOptions() {
    return {
      outlined: true,
      dense: true,
      color: "secondary",
      required: true,
    };
  }
}
